import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencyCode } from '../utils/formatters';

const API = '/api';

export default function CreateVehicle({ 
    user, editVehicle, onBack, onSaved, operatorDetails,
    vehicleTypes, fuelTypes, vehicleColors, vehicleStatuses,
    vehicleMakes = [], vehicleModels = [], seatingCapacities = [],
    documentTypes = []
}) {
    const [activeTab, setActiveTab] = useState('Vehicle Details');
    const [form, setForm] = useState({
        vehicleCode: '',
        plateNumber: '',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        vehicleType: '',
        fuelType: '',
        vehicleStatus: 'Active',
        color: '',
        purchasePrice: '',
        acquisitionDate: '',
        depreciationMonthly: '',
        gpsMonthly: '',
        dashcamMonthly: '',
        insuranceMonthly: '',
        insuranceExpiry: '',
        otherCostsMonthly: '',
        registrationNumber: '',
        registrationExpiry: '',
        vehicleLicenseNumber: '',
        vehicleLicenseExpiry: '',
        fuelStickerNumber: '',
        insuranceProvider: '',
        policyNumber: '',
        fleetReceivedDate: '',
        efficiency: '',
        photo: '',
        documents: []
    });

    const [documentsList, setDocumentsList] = useState([{ name: '', file: null }]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusOpen, setStatusOpen] = useState(false);

    const tabs = ['Vehicle Details', 'Registration & Insurance', 'Financials'];

    useEffect(() => {
        if (editVehicle) {
            setForm({
                ...editVehicle,
                vehicleCode: editVehicle.vehicleCode || '',
                year: editVehicle.year || new Date().getFullYear(),
                vehicleStatus: editVehicle.vehicleStatus || 'Active',
                purchasePrice: editVehicle.purchasePrice?.toString() || '',
                acquisitionDate: editVehicle.acquisitionDate ? editVehicle.acquisitionDate.split('T')[0] : '',
                depreciationMonthly: editVehicle.depreciationMonthly?.toString() || '',
                gpsMonthly: editVehicle.gpsMonthly?.toString() || '',
                dashcamMonthly: editVehicle.dashcamMonthly?.toString() || '',
                insuranceMonthly: editVehicle.insuranceMonthly?.toString() || '',
                insuranceExpiry: editVehicle.insuranceExpiry ? editVehicle.insuranceExpiry.split('T')[0] : '',
                otherCostsMonthly: editVehicle.otherCostsMonthly?.toString() || '',
                seatingCapacity: editVehicle.seatingCapacity?.toString() || '',
                registrationNumber: editVehicle.registrationNumber || '',
                registrationExpiry: editVehicle.registrationExpiry ? editVehicle.registrationExpiry.split('T')[0] : '',
                vehicleLicenseNumber: editVehicle.vehicleLicenseNumber || '',
                vehicleLicenseExpiry: editVehicle.vehicleLicenseExpiry ? editVehicle.vehicleLicenseExpiry.split('T')[0] : '',
                fuelStickerNumber: editVehicle.fuelStickerNumber || '',
                insuranceProvider: editVehicle.insuranceProvider || '',
                policyNumber: editVehicle.policyNumber || '',
                fleetReceivedDate: editVehicle.fleetReceivedDate ? editVehicle.fleetReceivedDate.split('T')[0] : '',
                efficiency: editVehicle.efficiency || '',
                photo: editVehicle.photo || '',
                documents: editVehicle.documents || []
            });
        }
    }, [editVehicle]);

    const handleAddDocument = () => setDocumentsList(prev => [...prev, { name: '', file: null }]);
    
    const handleDocumentChange = (index, field, value) => {
        const updated = [...documentsList];
        updated[index][field] = value;
        setDocumentsList(updated);
    };

    const handleRemoveDocument = (index) => {
        const updated = [...documentsList];
        updated.splice(index, 1);
        setDocumentsList(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Resolve operatorId — may be an object {_id: ...} or a plain string
        const resolvedOperatorId =
            (typeof user?.operatorId === 'object' ? user.operatorId?._id : user?.operatorId) ||
            (typeof user?._id === 'object' ? user._id?._id : user?._id);

        if (!resolvedOperatorId) {
            setError('Operator ID is missing. Please re-login and try again.');
            setLoading(false);
            return;
        }

        // Keys to skip in the generic loop — handled separately below
        const skipKeys = new Set(['_id', '__v', 'operatorId', 'documents', 'createdAt', 'updatedAt']);

        const data = new FormData();

        Object.keys(form).forEach(key => {
            if (skipKeys.has(key)) return;
            const val = form[key];
            if (val !== undefined && val !== null) {
                data.append(key, val);
            }
        });

        // Append operatorId cleanly
        data.append('operatorId', resolvedOperatorId);

        // Append existing documents array as JSON so backend can preserve them
        data.append('existingDocuments', JSON.stringify(form.documents || []));

        // Numerics
        data.set('purchasePrice', parseFloat(form.purchasePrice) || 0);
        data.set('year', parseInt(form.year) || new Date().getFullYear());
        data.set('depreciationMonthly', parseFloat(form.depreciationMonthly) || 0);
        data.set('gpsMonthly', parseFloat(form.gpsMonthly) || 0);
        data.set('dashcamMonthly', parseFloat(form.dashcamMonthly) || 0);
        data.set('insuranceMonthly', parseFloat(form.insuranceMonthly) || 0);
        if (form.insuranceExpiry) data.set('insuranceExpiry', form.insuranceExpiry);
        data.set('otherCostsMonthly', parseFloat(form.otherCostsMonthly) || 0);
        data.set('seatingCapacity', parseInt(form.seatingCapacity) || 0);

        if (form.registrationNumber) data.set('registrationNumber', form.registrationNumber);
        if (form.registrationExpiry) data.set('registrationExpiry', form.registrationExpiry);
        if (form.vehicleLicenseNumber) data.set('vehicleLicenseNumber', form.vehicleLicenseNumber);
        if (form.vehicleLicenseExpiry) data.set('vehicleLicenseExpiry', form.vehicleLicenseExpiry);
        if (form.fuelStickerNumber) data.set('fuelStickerNumber', form.fuelStickerNumber);
        if (form.insuranceProvider) data.set('insuranceProvider', form.insuranceProvider);
        if (form.policyNumber) data.set('policyNumber', form.policyNumber);
        if (form.fleetReceivedDate) data.set('fleetReceivedDate', form.fleetReceivedDate);
        if (form.efficiency) data.set('efficiency', form.efficiency);

        // New document files
        const documentNames = [];
        documentsList.forEach(doc => {
            if (doc.file) {
                data.append('documents', doc.file);
                documentNames.push(doc.name || 'Document');
            }
        });
        if (documentNames.length > 0) {
            data.append('documentNames', JSON.stringify(documentNames));
        }

        try {
            if (editVehicle) {
                await axios.put(`${API}/vehicles/${editVehicle._id}`, data);
            } else {
                await axios.post(`${API}/vehicles`, data);
            }
            onSaved();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save vehicle');
        } finally {
            setLoading(false);
        }
    };

    const Icon = ({ d, size = 16, strokeWidth = 2, color="currentColor" }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
        </svg>
    );

    const icons = {
        truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
        file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
    };

    const selectedStatus = vehicleStatuses.find(s => s.value === form.vehicleStatus) || vehicleStatuses[0] || {};
    const currencyCode = getCurrencyCode(operatorDetails?.country);

    return (
        <div className="create-driver-wrapper">
            {/* Using the exact same styling classes from CreateDriver for consistency */}
            <style>{`
                .create-driver-wrapper { height: 100%; width: 100%; overflow: hidden; }
                .driver-ts-container { display: flex; height: 100%; background: #f8fafc; font-family: 'Inter', sans-serif; }
                .ts-sidebar { width: 280px; background: white; border-right: 1px solid #e2e8f0; padding: 32px 16px; display: flex; flex-direction: column; gap: 8px; flex-shrink: 0; height: 100%; overflow-y: auto; }
                .ts-nav-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 12px; border: none; background: transparent; color: #64748b; font-size: 0.9375rem; font-weight: 600; cursor: pointer; text-align: left; transition: all 0.2s; }
                .ts-nav-item:hover:not(:disabled) { background: #f8fafc; color: #16539C; }
                .ts-nav-item.active { background: #eef2ff; color: #16539C; }
                
                .ts-main { flex: 1; padding: 40px; overflow-y: auto; display: flex; flex-direction: column; gap: 32px; }
                .ts-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
                .ts-title-group h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .ts-title-group p { font-size: 14px; color: #64748b; margin: 4px 0 0; }
                .back-btn-premium { display: flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: white; color: #64748b; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.2s; }
                .back-btn-premium:hover { background: #f8fafc; color: #1e293b; border-color: #cbd5e1; }
                
                .form-panel { background: white; border-radius: 20px; padding: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid #e2e8f0; max-width: 1000px; }
                .section-title { font-size: 1.125rem; font-weight: 700; margin-bottom: 24px; color: #1e293b; }
                
                .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
                .grid-3 { grid-template-columns: repeat(3, 1fr); }
                .span-2 { grid-column: span 2; }
                .form-group { display: flex; flex-direction: column; gap: 8px; }
                .form-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
                
                .form-input, .form-select { padding: 12px 16px; border: 1.5px solid #e2e8f0; border-radius: 12px; font-size: 14px; transition: all 0.2s; background: #fff; color: #1e293b; outline: none; }
                .form-input:focus, .form-select:focus { border-color: #16539C; box-shadow: 0 0 0 4px rgba(22, 83, 156, 0.1); }
                
                .footer-actions { display: flex; justify-content: flex-end; gap: 16px; margin-top: 40px; padding-top: 32px; border-top: 1px solid #e2e8f0; }
                .btn-premium { padding: 12px 28px; border-radius: 12px; font-weight: 700; font-size: 14px; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 10px; }
                .btn-cancel { background: white; border: 1.5px solid #e2e8f0; color: #64748b; }
                .btn-cancel:hover { background: #f1f5f9; color: #1e293b; }
                .btn-save { background: #16539C; border: none; color: white; box-shadow: 0 4px 6px -1px rgba(22, 83, 156, 0.3); }
                .btn-save:hover { background: #11427D; transform: translateY(-1px); }
                .spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.6s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .vm-status-wrap { position: relative; }
                .vm-status-btn { width: 100%; padding: 13px 14px; border: 1.5px solid #e2e8f0; border-radius: 12px; background: #f8fafc; cursor: pointer; display: flex; align-items: center; gap: 10px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; color: #0f172a; }
                .vm-status-btn:hover { border-color: #00B2A9; background: #fff; }
                .vm-status-pill { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
                .vm-status-dropdown { position: absolute; top: calc(100% + 8px); left: 0; right: 0; z-index: 200; background: white; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 20px 40px -10px rgba(0,0,0,0.15); padding: 8px; }
                .vm-status-opt { display: flex; align-items: center; gap: 10px; padding: 11px 14px; border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 600; color: #334155; }
                .vm-status-opt:hover { background: #f1f5f9; }
                
                .vm-color-panel { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 18px; }
                .vm-color-swatches { display: flex; flex-wrap: wrap; gap: 9px; margin-bottom: 14px; }
                .vm-swatch { width: 32px; height: 32px; border-radius: 8px; cursor: pointer; border: 2.5px solid white; box-shadow: 0 0 0 1px #d1d5db; position: relative; }
                .vm-swatch.active { box-shadow: 0 0 0 3px #00B2A9; transform: scale(1.15); }
                .vm-colorpicker-preview { width: 40px; height: 40px; border-radius: 10px; border: 2.5px solid white; box-shadow: 0 0 0 1px #d1d5db; flex-shrink: 0; position: relative; overflow: hidden; }
                .vm-colorpicker-preview input { position: absolute; inset: -8px; width: 140%; height: 140%; cursor: pointer; border: none; padding: 0; }
            `}</style>

            <div className="driver-ts-container">
                <aside className="ts-sidebar">
                    <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', paddingLeft: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#16539C' }}>Vehicle Profile</span>
                    </div>
                    {tabs.map(tab => (
                        <button key={tab} className={`ts-nav-item ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                            {tab === 'Vehicle Details' && <Icon d={icons.truck} />}
                            {tab === 'Registration & Insurance' && <Icon d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />}
                            {tab === 'Financials' && <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />}
                            <span>{tab}</span>
                        </button>
                    ))}
                </aside>

                <main className="ts-main">
                    <header className="ts-header">
                        <div className="ts-title-group">
                            <h1>{editVehicle ? 'Edit Vehicle Profile' : 'Register New Vehicle'}</h1>
                            <p>{editVehicle ? 'Update the vehicle details below' : 'Fill in details to add a new vehicle'}</p>
                        </div>
                        <button className="back-btn-premium" onClick={onBack}>
                            <Icon d="M19 12H5M12 19l-7-7 7-7" size={16} strokeWidth={2.5} /> Back to List
                        </button>
                    </header>

                    {error && (
                        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '16px 24px', borderRadius: 16, border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, fontWeight: 500 }}>
                            <Icon d="M12 8v4M12 16h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" strokeWidth={2} />
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                        <div className="form-panel">
                            {activeTab === 'Vehicle Details' && (
                                <>
                                    <div className="section-title">Core Information</div>
                                    <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                                        <div style={{ width: 80, height: 80, borderRadius: 16, background: '#f1f5f9', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                                            {form.photo ? (
                                                <img src={form.photo} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <Icon d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" size={28} color="#94a3b8" />
                                            )}
                                            <input 
                                                type="file" 
                                                accept="image/*"
                                                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        const reader = new FileReader();
                                                        reader.onload = (evt) => setForm({ ...form, photo: evt.target.result });
                                                        reader.readAsDataURL(file);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <h4 style={{ margin: '0 0 4px', fontSize: 13, color: '#1e293b', fontWeight: 600 }}>Upload Photo</h4>
                                            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Click or drag image to upload.</p>
                                        </div>
                                    </div>

                                    <div className="form-grid" style={{ marginBottom: 32 }}>
                                        <div className="form-group">
                                            <label className="form-label">Vehicle ID / Code</label>
                                            <input className="form-input" placeholder="e.g. V-001" value={form.vehicleCode} onChange={e => setForm({ ...form, vehicleCode: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Plate Number *</label>
                                            <input className="form-input" required placeholder="e.g. DXB-1234" value={form.plateNumber} onChange={e => setForm({ ...form, plateNumber: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Manufacturing Year</label>
                                            <input className="form-input" type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Fleet Received Date</label>
                                            <input className="form-input" type="date" value={form.fleetReceivedDate} onChange={e => setForm({ ...form, fleetReceivedDate: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Make / Manufacturer *</label>
                                            <select className="form-select" required value={form.make} onChange={e => setForm({ ...form, make: e.target.value })}>
                                                <option value="">Select Make...</option>
                                                {vehicleMakes.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {form.make && !vehicleMakes.find(m => m.name === form.make) && <option value={form.make}>{form.make}</option>}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Model Name</label>
                                            <select className="form-select" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}>
                                                <option value="">Select Model...</option>
                                                {vehicleModels.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {form.model && !vehicleModels.find(m => m.name === form.model) && <option value={form.model}>{form.model}</option>}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Vehicle Category</label>
                                            <select className="form-select" value={form.vehicleType} onChange={e => setForm({ ...form, vehicleType: e.target.value })}>
                                                <option value="">Select Category...</option>
                                                {vehicleTypes.map(vt => <option key={vt._id} value={vt.name}>{vt.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Fuel Type</label>
                                            <select className="form-select" value={form.fuelType} onChange={e => setForm({ ...form, fuelType: e.target.value })}>
                                                <option value="">Select Fuel Type...</option>
                                                {fuelTypes.map(ft => <option key={ft._id} value={ft.name}>{ft.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Fuel Sticker Number</label>
                                            <input className="form-input" placeholder="e.g. FS-8890" value={form.fuelStickerNumber} onChange={e => setForm({ ...form, fuelStickerNumber: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Seating Capacity</label>
                                            <select className="form-select" value={form.seatingCapacity} onChange={e => setForm({ ...form, seatingCapacity: e.target.value })}>
                                                <option value="">Select Capacity...</option>
                                                {seatingCapacities.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                                {form.seatingCapacity && !seatingCapacities.find(c => c.name === String(form.seatingCapacity)) && <option value={form.seatingCapacity}>{form.seatingCapacity}</option>}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Efficiency</label>
                                            <input className="form-input" placeholder="e.g. 15 km/l" value={form.efficiency} onChange={e => setForm({ ...form, efficiency: e.target.value })} />
                                        </div>
                                    </div>

                                    <div className="form-grid" style={{ marginBottom: 32 }}>
                                        <div className="form-group">
                                            <label className="form-label">Operational Status</label>
                                            <div className="vm-status-wrap">
                                                <button type="button" className="vm-status-btn" onClick={() => setStatusOpen(!statusOpen)}>
                                                    <span className="vm-status-pill" style={{ background: selectedStatus.dot }} />
                                                    <span style={{ fontWeight: 700 }}>{selectedStatus.label || 'Select Status'}</span>
                                                    <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>▾</span>
                                                </button>
                                                {statusOpen && (
                                                    <div className="vm-status-dropdown">
                                                        {vehicleStatuses.map(opt => (
                                                            <div key={opt.value} className={`vm-status-opt${form.vehicleStatus === opt.value ? ' active' : ''}`}
                                                                onClick={() => { setForm({ ...form, vehicleStatus: opt.value }); setStatusOpen(false); }}>
                                                                <span className="vm-status-pill" style={{ background: opt.dot }} />
                                                                {opt.label}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="form-group">
                                            <label className="form-label">Vehicle Color</label>
                                            <div className="vm-color-panel" style={{ padding: 12 }}>
                                                <div className="vm-color-swatches" style={{ marginBottom: 0 }}>
                                                    {vehicleColors.slice(0, 7).map(c => (
                                                        <div key={c} className={`vm-swatch${form.color === c ? ' active' : ''}`}
                                                            style={{ background: c }} onClick={() => setForm({ ...form, color: c })} title={c} />
                                                    ))}
                                                    <input type="color" style={{ width: 32, height: 32, padding: 0, border: '2px solid white', borderRadius: 8, cursor: 'pointer' }} value={form.color || '#ffffff'} onChange={e => setForm({ ...form, color: e.target.value.toUpperCase() })} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Documents Upload Section */}
                                    <div className="form-group span-2" style={{ background: '#f8fafc', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                            <label className="form-label" style={{ margin: 0 }}>Vehicle Documents</label>
                                            <button type="button" onClick={handleAddDocument} style={{ background: '#eef2ff', color: '#16539C', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon d="M12 5v14M5 12h14" /> Add Document
                                            </button>
                                        </div>
                                        
                                        {form.documents?.length > 0 && (
                                            <div style={{ marginBottom: 20 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>Existing Documents</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    {form.documents.map((doc, idx) => (
                                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                                            <Icon d={icons.file} size={18} />
                                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{doc.name || 'Document'}</span>
                                                            <a href={`/api/uploads/${doc.file}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', fontSize: 12, color: '#16539C', textDecoration: 'none', fontWeight: 600 }}>View</a>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {documentsList.map((doc, index) => (
                                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <select 
                                                        className="form-select" 
                                                        value={doc.name} 
                                                        onChange={e => handleDocumentChange(index, 'name', e.target.value)} 
                                                        style={{ flex: 1 }}
                                                    >
                                                        <option value="">Select Document Type...</option>
                                                        {documentTypes.map(dt => (
                                                            <option key={dt._id} value={dt.name}>{dt.name}</option>
                                                        ))}
                                                        {doc.name && !documentTypes.find(dt => dt.name === doc.name) && (
                                                            <option value={doc.name}>{doc.name}</option>
                                                        )}
                                                    </select>
                                                    <input 
                                                        className="form-input" 
                                                        type="file" 
                                                        onChange={e => handleDocumentChange(index, 'file', e.target.files[0])} 
                                                        style={{ flex: 1 }}
                                                    />
                                                    {documentsList.length > 1 && (
                                                        <button type="button" onClick={() => handleRemoveDocument(index)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: 10, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Icon d="M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'Registration & Insurance' && (
                                <>
                                    <div className="section-title">Registration Details</div>
                                    <div className="form-grid" style={{ marginBottom: 32 }}>
                                        <div className="form-group">
                                            <label className="form-label">Registration Number</label>
                                            <input className="form-input" placeholder="e.g. REG-445566" value={form.registrationNumber} onChange={e => setForm({ ...form, registrationNumber: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Daftar Expiry</label>
                                            <input className="form-input" type="date" value={form.registrationExpiry} onChange={e => setForm({ ...form, registrationExpiry: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Vehicle License / Permit No.</label>
                                            <input className="form-input" placeholder="e.g. LIC-998877" value={form.vehicleLicenseNumber} onChange={e => setForm({ ...form, vehicleLicenseNumber: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">License Expiry Date</label>
                                            <input className="form-input" type="date" value={form.vehicleLicenseExpiry} onChange={e => setForm({ ...form, vehicleLicenseExpiry: e.target.value })} />
                                        </div>
                                    </div>

                                    <div className="section-title">Insurance Details</div>
                                    <div className="form-grid" style={{ marginBottom: 32 }}>
                                        <div className="form-group">
                                            <label className="form-label">Insurance Provider</label>
                                            <input className="form-input" placeholder="e.g. Al Ahliya" value={form.insuranceProvider} onChange={e => setForm({ ...form, insuranceProvider: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Policy Number</label>
                                            <input className="form-input" placeholder="e.g. POL-123456" value={form.policyNumber} onChange={e => setForm({ ...form, policyNumber: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Insurance Expiry Date</label>
                                            <input className="form-input" type="date" value={form.insuranceExpiry} onChange={e => setForm({ ...form, insuranceExpiry: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Monthly Insurance Cost ({currencyCode})</label>
                                            <input className="form-input" type="number" step="0.01" value={form.insuranceMonthly} onChange={e => setForm({ ...form, insuranceMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'Financials' && (
                                <>
                                    <div className="section-title">Asset Valuation</div>
                                    <div className="form-grid" style={{ marginBottom: 32 }}>
                                        <div className="form-group">
                                            <label className="form-label">Purchase Price ({currencyCode})</label>
                                            <input className="form-input" type="number" step="0.01" value={form.purchasePrice} onChange={e => setForm({ ...form, purchasePrice: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Acquisition Date</label>
                                            <input className="form-input" type="date" value={form.acquisitionDate} onChange={e => setForm({ ...form, acquisitionDate: e.target.value })} />
                                        </div>
                                    </div>

                                    <div className="section-title">Monthly Fixed Costs ({currencyCode})</div>
                                    <div className="form-grid grid-3">
                                        {[
                                            { label: 'Depreciation', key: 'depreciationMonthly', type: 'number', step: '0.01' },
                                            { label: 'GPS Fee', key: 'gpsMonthly', type: 'number', step: '0.01' },
                                            { label: 'Dashcam Fee', key: 'dashcamMonthly', type: 'number', step: '0.01' },
                                            { label: 'Other Costs', key: 'otherCostsMonthly', type: 'number', step: '0.01' },
                                        ].map(({ label, key, type, step }) => (
                                            <div className="form-group" key={key}>
                                                <label className="form-label">{label}</label>
                                                <input className="form-input" type={type} step={step} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            <div className="footer-actions">
                                <button type="button" className="btn-premium btn-cancel" onClick={onBack}>Discard Changes</button>
                                <button type="submit" className="btn-premium btn-save" disabled={loading}>
                                    {loading ? <div className="spinner"></div> : <Icon d="M5 13l4 4L19 7" size={18} strokeWidth={3} color="white" />}
                                    <span style={{ marginLeft: 8 }}>{loading ? 'Processing...' : editVehicle ? 'Update Vehicle' : 'Save Vehicle'}</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
}
